import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    ListItemButton
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SecurityIcon from '@mui/icons-material/Security';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import PeopleIcon from '@mui/icons-material/People';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { nextPreload, useTranslation } from '@fanfanlo';
import { useRouter } from 'next/router';
import { matrixClient } from '../../client';
import toast from 'react-hot-toast';

export const RoomSettingsContent = ({ roomId }: { roomId: string }) => {
    const { t } = useTranslation('homepage/im/components/room-settings/content');
    const router = useRouter();
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
    const [leaving, setLeaving] = useState(false);

    const settingsItems = [
        {
            id: 'basic',
            icon: <SettingsIcon />,
            text: t('RoomSettingsContent.tabs.basic'),
            path: `/im/room/room-settings/basic?id=${roomId}`
        },
        {
            id: 'permission',
            icon: <SecurityIcon />,
            text: t('RoomSettingsContent.tabs.permission'),
            path: `/im/room/room-settings/permission?id=${roomId}`
        },
        {
            id: 'privacy',
            icon: <PrivacyTipIcon />,
            text: t('RoomSettingsContent.tabs.privacy'),
            path: `/im/room/room-settings/privacy?id=${roomId}`
        },
        {
            id: 'member',
            icon: <PeopleIcon />,
            text: t('RoomSettingsContent.tabs.member'),
            path: `/im/room/room-settings/member?id=${roomId}`
        }
    ];

  useEffect(() => {
    nextPreload(router, '/im');
  }, []);
    const handleLeaveRoom = async () => {
        setLeaving(true);
        try {
            await matrixClient.client.leave(roomId);
            toast.success(t('RoomSettingsContent.leaveRoom.success'));
            router.push('/im');
        } catch (error) {
            toast.error(t('RoomSettingsContent.leaveRoom.error'));
        } finally {
            setLeaving(false);
            setLeaveDialogOpen(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 'md', mx: 'auto', mt: 4, px: 2 }}>
            <Paper elevation={0} sx={{ mb: 3 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                    {t('RoomSettingsContent.title')}
                </Typography>
                <List>
                    {settingsItems.map((item, index) => (
                        <React.Fragment key={item.id}>
                            <ListItem disablePadding>
                                <ListItemButton onClick={() => router.push(item.path)}>
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.text} />
                                    <ChevronRightIcon color="action" />
                                </ListItemButton>
                            </ListItem>
                            {index < settingsItems.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </List>
            </Paper>
            <Box sx={{ mt: 4, p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={() => setLeaveDialogOpen(true)}
                    disabled={leaving}
                >
                    {t('RoomSettingsContent.leaveRoom.button')}
                </Button>
            </Box>
            <Dialog
                open={leaveDialogOpen}
                onClose={() => setLeaveDialogOpen(false)}
                aria-labelledby="leave-room-dialog-title"
                aria-describedby="leave-room-dialog-description"
            >
                <DialogTitle id="leave-room-dialog-title">
                    {t('RoomSettingsContent.leaveRoom.button')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="leave-room-dialog-description">
                        {t('RoomSettingsContent.leaveRoom.confirm')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLeaveDialogOpen(false)} disabled={leaving}>
                        {t('common.cancel', '取消')}
                    </Button>
                    <Button onClick={handleLeaveRoom} color="error" disabled={leaving}>
                        {t('RoomSettingsContent.leaveRoom.button')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}; 